import type { ReferenceStore } from '..';
import type { DataForHAR } from '@readme/oas-to-har/lib/types';
import type Oas from 'oas';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

export interface ReferenceFormSliceState {
  /**
   * Map of operation keys (path + method) to their expanded response status code.
   * Used to track which response schema panel is expanded for component state sync.
   * Key: `${operation.path}-${operation.method}`, Value: status code string or null
   */
  expandedResponseStatusCode: Record<string, string | null>;

  /**
   * The full server URL, injected with any default and user-specific variables.
   * Only present if there is an API definition loaded via `InitializeReferenceStore`.
   */
  fullServerUrl: ReturnType<Oas['url']>;

  /**
   * Data that pertains to our raw JSON editor
   */
  rawJsonEditor: {
    /** Indicates whether raw JSON editor is enabled or not */
    isEnabled: boolean;

    /**
     * The valid, parseable JSON that will be surfaced in the request body
     *
     * @example { test: 2 }
     */
    parsedJson: Record<string, unknown>;

    /**
     * The "raw" JSON user input.
     * This is a string so it can be valid or invalid.
     *
     * @example '{"test": 1}'
     */
    stringifiedJson: string;

    /**
     * Any JSON validation errors when attempting to take
     * `stringifiedJson` and parse it into `parsedJson`
     *
     * @example "Expected ':' after property name in JSON at position 7"
     */
    validationError?: string;
  };

  /**
   * Data that pertains to our schema form editor
   */
  schemaEditor: {
    /**
     * Object containing the schema form data as HAR
     * the way that `@readme/oas-to-har` expects it
     *
     * @example { body: { name: 'buster' }, path: { petId: 123 } }
     */
    data: DataForHAR;

    /**
     * Object containing explorer file uploads.
     *
     * @note In our explorer, any files sent as body parameters
     * are stored as a data URL within the `data.body` object
     * (defined above)
     *
     * @example { 'openapi.json': {} }
     */
    files: Record<string, unknown>;

    /** Indicates whether schema form is dirty or not */
    isDirty: boolean;
  };

  /**
   * Map of schema IDs to their selected oneOf/anyOf option indices.
   * Used to deterministically compute defaults when switching between options.
   * Key: idSchema.$id, Value: selected index
   */
  selectedOptionIndices: Record<string, number>;

  /**
   * Map of operation keys (path + method) to their selected request content type.
   * Used by docs-only request schema views, like callbacks, that shouldn't update
   * the executable Try It request Content-Type header.
   * Key: `${operation.path}-${operation.method}`, Value: selected content type or null
   */
  selectedRequestContentType: Record<string, string | null>;

  /**
   * Variables that track the named example the
   * user has selected. Used in pairing response examples
   * with request examples.
   */
  selectedRequestExampleKey: string | null;

  /**
   * Map of operation keys (path + method) to their selected response content type.
   * Used to synchronize Accept header, response schema dropdown, and response example.
   * Key: `${operation.path}-${operation.method}`, Value: selected content type or null
   */
  selectedResponseContentType: Record<string, string | null>;

  /**
   * Map of operation keys (path + method) to their selected response example.
   * Used to coordinate response example selection with content type and schema panel.
   * Key: `${operation.path}-${operation.method}`, Value: example info or null
   */
  selectedResponseExample: Record<
    string,
    {
      mediaType: string;
      status: string;
      id: string;
    } | null
  >;
}

export interface ReferenceFormSliceActions {
  /**
   * Clears the selected oneOf/anyOf option index for a specific schema
   */
  clearSelectedOptionIndex: (schemaId: string) => void;

  /**
   * Returns the current form data, accounting for
   * if the body should be sourced from the raw JSON editor.
   */
  getCurrentFormData: () => ReferenceFormSliceState['schemaEditor']['data'];

  /**
   * Initializes the store. This generally runs every time a new endpoint is selected.
   */
  initialize: (opts?: {
    fullServerUrl?: ReferenceFormSliceState['fullServerUrl'];
    /**
     * Optional argument for populating default server variables
     * (e.g., `schemaEditor.data.server`).
     */
    server?: NonNullable<ReferenceFormSliceState['schemaEditor']['data']['server']>;
  }) => void;

  /**
   * Resets form store, with optional object with flags for partial resets.
   * If zero arguments (or empty object) are passed, `initialize` is run.
   */
  reset: (opts?: { jsonForm?: boolean; requestExample?: boolean; schemaForm?: boolean }) => void;

  /**
   * Updates the expanded response status code for a specific operation.
   * When a status code is set, it triggers the corresponding panel to expand.
   */
  setExpandedResponseStatusCode: (operationKey: string, statusCode: string | null) => void;

  /**
   * Sets the selected oneOf/anyOf option index for a specific schema
   */
  setSelectedOptionIndex: (schemaId: string, index: number) => void;

  /**
   * Updates the selected request content type for a specific operation.
   * Used by docs-only request schema views, like callbacks.
   */
  setSelectedRequestContentType: (operationKey: string, contentType: string | null) => void;

  /**
   * Updates the selected response content type for a specific operation.
   * Used to synchronize Accept header, response schema dropdown, and response example.
   */
  setSelectedResponseContentType: (operationKey: string, contentType: string | null) => void;

  /**
   * Updates the selected response example for a specific operation.
   * This is called when a user selects an example from the dropdown.
   */
  setSelectedResponseExample: (
    operationKey: string,
    example: {
      mediaType: string;
      status: string;
      id: string;
    } | null,
  ) => void;

  /**
   * Enables/disables the raw JSON editor and also clears out
   * the currently selected request example and any dirty form data
   */
  toggleJsonEditor: () => void;

  /**
   * Records any updates made in the raw JSON editor
   */
  updateRawJsonFormData: (rawJsonData: string) => void;

  /**
   * Updates the currently selected request example, and takes its payload
   * and merges it with the schema form editor and/or raw JSON editor.
   * If `null` is passed as the `key`, it clears the current editor.
   */
  updateRequestExample: (
    /** The key (i.e., identifier) of the request example within the operation */
    key: ReferenceFormSliceState['selectedRequestExampleKey'],
    /** The request example payload */
    requestBody?: unknown,
    /** Indicates whether the request example payload is form-encoded or not */
    isFormEncoded?: boolean,
  ) => void;

  /**
   * Records any updates made in the schema form editor
   */
  updateSchemaFormData: (
    data: ReferenceFormSliceState['schemaEditor']['data'],
    files?: ReferenceFormSliceState['schemaEditor']['files'],
  ) => void;

  /**
   * Updates the server selection in our HAR store (e.g., `schemaEditor.data.server`)
   */
  updateServer: (
    newServer: ReferenceFormSliceState['schemaEditor']['data']['server'],
    apiDefinition: Oas | undefined,
    prioritizeNewServer?: boolean,
  ) => void;
}

export interface ReferenceFormSlice {
  /**
   * State slice containing fields and actions that are relevant when reading/writing
   * form data in the Reference. This slice includes data about the schema form,
   * raw JSON editor, server variable input, and any other form input data required for
   * constructing the HAR (minus authentication, which is stored in the `auth` slice).
   */
  form: ReferenceFormSliceActions & ReferenceFormSliceState;
}

export const initialState = {
  fullServerUrl: '',
  rawJsonEditor: {
    isEnabled: false,
    parsedJson: {},
    stringifiedJson: '{}',
  },
  schemaEditor: {
    data: {
      server: { selected: 0, variables: {} },
    },
    files: {},
    isDirty: false,
  },
  selectedOptionIndices: {},
  selectedRequestExampleKey: null,
  selectedRequestContentType: {},
  selectedResponseContentType: {},
  expandedResponseStatusCode: {},
  selectedResponseExample: {},
} as const satisfies ReferenceFormSliceState;

/**
 * Form state slice containing fields related to the schema form editor
 */
export const createReferenceFormSlice: StateCreator<
  ReferenceFormSlice & ReferenceStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ReferenceFormSlice
> = (set, get) => ({
  form: {
    ...initialState,

    getCurrentFormData: () => {
      if (!get().form.rawJsonEditor.isEnabled) {
        return get().form.schemaEditor.data;
      }

      return {
        ...get().form.schemaEditor.data,
        body: get().form.rawJsonEditor.parsedJson,
      };
    },

    reset: (opts = {}) => {
      if (!Object.keys(opts).length) {
        get().form.initialize();
        return;
      }

      if (opts.jsonForm) {
        get().form.updateRawJsonFormData('{}');
      }

      if (opts.requestExample) {
        get().form.updateRequestExample(null);
      }

      set(
        state => {
          if (opts.schemaForm) {
            state.form.schemaEditor = initialState.schemaEditor;
          }
        },
        false,
        actionLog('reset form slice', opts),
      );
    },

    toggleJsonEditor: () => {
      const isCurrentlyToggled = get().form.rawJsonEditor.isEnabled;
      get().form.updateRequestExample(null);

      set(
        state => {
          state.form.rawJsonEditor.isEnabled = !isCurrentlyToggled;
        },
        false,
        actionLog('toggleJsonEditor'),
      );
    },

    updateRawJsonFormData: rawJsonData => {
      set(
        state => {
          let parsedJson;
          try {
            parsedJson = JSON.parse(rawJsonData);
            state.form.rawJsonEditor.stringifiedJson = rawJsonData;
            state.form.rawJsonEditor.parsedJson = parsedJson;
            state.form.rawJsonEditor.validationError = undefined;
          } catch (err) {
            state.form.rawJsonEditor.stringifiedJson = rawJsonData;
            state.form.rawJsonEditor.validationError = err.message;
          }
        },
        false,
        actionLog('updateRawJsonFormData', { rawJsonData }),
      );
    },

    updateRequestExample: (key, requestBody, isFormEncoded) => {
      const isJsonEditorEnabled = get().form.rawJsonEditor.isEnabled;

      if (!isJsonEditorEnabled) {
        get().form.updateSchemaFormData({
          ...get().form.schemaEditor.data,
          ...(isFormEncoded ? { formData: requestBody || {} } : { body: requestBody }),

          // logic to ensure that bodies are properly cleared out
          ...(requestBody === undefined && isFormEncoded === undefined ? { body: undefined, formData: undefined } : {}),
        });
      } else {
        get().form.updateRawJsonFormData(JSON.stringify(requestBody === undefined ? {} : requestBody, null, '\t'));
      }

      set(
        state => {
          state.form.selectedRequestExampleKey = key;
        },
        false,
        actionLog('updateRequestExample', { key, requestBody, isFormEncoded }),
      );
    },

    updateSchemaFormData: (data, files = {}) => {
      const previousData = get().form.schemaEditor.data;
      const previousFiles = get().form.schemaEditor.files;
      const updatedData = {
        ...previousData,
        ...data,
        ...(data.header
          ? {
              header: {
                ...previousData.header,
                ...data.header,
              },
            }
          : {}),
      };

      set(
        state => {
          state.form.schemaEditor = {
            data: updatedData,
            files: { ...previousFiles, ...files },
            isDirty: true,
          };
          state.selectedHar = null;
        },
        false,
        actionLog('updateSchemaFormData', { data, files }),
      );
    },

    updateServer: (newServer, apiDefinition, prioritizeNewServer = true) => {
      let updatedServer: ReferenceFormSliceState['schemaEditor']['data']['server'];

      if (prioritizeNewServer) {
        updatedServer = {
          ...initialState.schemaEditor.data.server,
          ...get().form.schemaEditor.data.server,
          ...newServer,
        };
      } else {
        updatedServer = {
          ...initialState.schemaEditor.data.server,
          ...newServer,
          ...get().form.schemaEditor.data.server,
        };
      }

      set(
        state => {
          state.form.schemaEditor.data.server = updatedServer;

          const fullServerUrl = apiDefinition?.url(updatedServer?.selected, updatedServer?.variables);
          if (fullServerUrl) {
            state.form.fullServerUrl = fullServerUrl;
          }
        },
        false,
        actionLog('updateServer', {
          newServer,
          prioritizeNewServer,
        }),
      );
    },

    initialize: (opts = {}) => {
      set(
        state => {
          const server = opts?.server || initialState.schemaEditor.data.server;
          state.form = {
            ...state.form,
            ...initialState,
            fullServerUrl: opts?.fullServerUrl || '',
            schemaEditor: {
              data: {
                server,
              },
              files: {},
              isDirty: false,
            },
            selectedRequestContentType: {},
            selectedResponseContentType: {},
            expandedResponseStatusCode: {},
            selectedResponseExample: {},
          };
          state.selectedHar = null;
        },
        false,
        actionLog('initialize form slice', opts),
      );
    },

    setSelectedOptionIndex: (schemaId, index) => {
      set(
        state => {
          state.form.selectedOptionIndices[schemaId] = index;
        },
        false,
        actionLog('setSelectedOptionIndex', { schemaId, index }),
      );
    },

    clearSelectedOptionIndex: schemaId => {
      set(
        state => {
          delete state.form.selectedOptionIndices[schemaId];
        },
        false,
        actionLog('clearSelectedOptionIndex', { schemaId }),
      );
    },

    setSelectedRequestContentType: (operationKey, contentType) => {
      set(
        state => {
          if (contentType === null) {
            delete state.form.selectedRequestContentType[operationKey];
          } else {
            state.form.selectedRequestContentType[operationKey] = contentType;
          }
        },
        false,
        actionLog('setSelectedRequestContentType', { operationKey, contentType }),
      );
    },

    setSelectedResponseContentType: (operationKey, contentType) => {
      set(
        state => {
          if (contentType === null) {
            delete state.form.selectedResponseContentType[operationKey];
          } else {
            state.form.selectedResponseContentType[operationKey] = contentType;
          }

          // Clear response example if it doesn't match the new content type
          const currentExample = state.form.selectedResponseExample[operationKey];
          if (currentExample && contentType !== null && currentExample.mediaType !== contentType) {
            delete state.form.selectedResponseExample[operationKey];
          }
        },
        false,
        actionLog('setSelectedResponseContentType', { operationKey, contentType }),
      );
    },

    setExpandedResponseStatusCode: (operationKey, statusCode) => {
      set(
        state => {
          if (statusCode === null) {
            delete state.form.expandedResponseStatusCode[operationKey];
          } else {
            state.form.expandedResponseStatusCode[operationKey] = statusCode;
          }
        },
        false,
        actionLog('setExpandedResponseStatusCode', { operationKey, statusCode }),
      );
    },

    setSelectedResponseExample: (operationKey, example) => {
      set(
        state => {
          if (example === null) {
            delete state.form.selectedResponseExample[operationKey];
          } else {
            state.form.selectedResponseExample[operationKey] = example;

            state.form.selectedResponseContentType[operationKey] = example.mediaType;

            // Auto-expand the corresponding response schema panel
            state.form.expandedResponseStatusCode[operationKey] = example.status;
          }
        },
        false,
        actionLog('setSelectedResponseExample', { operationKey, example }),
      );
    },
  },
});
